/**
 * useMesaOrderAlerts — Aviso global de pedidos de mesa nuevos.
 *
 * QUÉ HACE:
 *  - Montado en MainLayout: pollea `mesasService.getOrdenes` cada 30 s (pausa
 *    con tab oculto y refresca al volver), sin importar la ruta activa.
 *  - Detecta órdenes ya ENVIADAS a cocina (excluye borradores `abierta`) que no
 *    se habían visto en la sesión y dispara el aviso compartido
 *    (`notifyOrderAlert("mesa")`): beep + voz + notificación según las
 *    preferencias de «Sonidos del POS». Al abrir la app solo siembra los IDs.
 *  - Solo se activa para usuarios con acceso a Caja (misma regla que la ruta
 *    `/caja`: roles + permiso `caja` desde `routeCatalog`).
 * QUÉ EXPORTA: useMesaOrderAlerts
 * QUÉ NO DEBE HACER:
 *  - No imprime ni cobra; no conoce el interior del módulo Mesas (usa su
 *    service público).
 *  - No renderiza UI.
 */
import { useCallback, useRef } from "react";
import { usePermissions } from "./usePermissions";
import { usePolling } from "./usePolling";
import { useStorageSignal } from "./useStorageSignal";
import { getAppRoute } from "@/router/routeCatalog";
import {
  ESTADOS_ORDEN_ACTIVA,
  etiquetaOrdenMesa,
  mesasService,
} from "@/features/mesas/services/mesas.service";
import { notifyOrderAlert } from "@/lib/alerts/orderAlerts";
import type { OrdenMesa } from "@/types/mesas";

/** Intervalo del polling global (30 s): control de costo al conectar backend. */
const REFRESH_MS = 30_000;

export function useMesaOrderAlerts(): void {
  const { canAccess } = usePermissions();
  const cajaRoute = getAppRoute("/caja");
  const canAlert = !!cajaRoute && canAccess(cajaRoute.roles, cajaRoute.module);
  /** IDs ya vistos: null hasta la primera carga (no suena al abrir la app). */
  const seenRef = useRef<Set<string> | null>(null);

  const tick = useCallback(async (): Promise<void> => {
    if (!canAlert) return;
    let ordenes: OrdenMesa[];
    try {
      ordenes = await mesasService.getOrdenes({
        estados: ESTADOS_ORDEN_ACTIVA,
      });
    } catch {
      return; // offline: el siguiente tick reintenta
    }

    // Solo órdenes ya enviadas a cocina: los borradores no son "pedido nuevo".
    const activas = ordenes.filter((o) => o.estado !== "abierta");
    const seen = seenRef.current;
    if (!seen) {
      seenRef.current = new Set(activas.map((o) => o.id));
      return;
    }

    const nuevas = activas.filter((o) => !seen.has(o.id));
    if (nuevas.length > 0) {
      const ultima = nuevas[nuevas.length - 1];
      const detalle = [etiquetaOrdenMesa(ultima), ultima.meseroNombre]
        .filter((p) => !!p && p.trim().length > 0)
        .join(" · ");
      notifyOrderAlert("mesa", {
        detalle,
        pedidoId: ultima.id,
        screen: "caja",
      });
    }
    for (const o of activas) seen.add(o.id);
  }, [canAlert]);

  usePolling(tick, {
    interval: REFRESH_MS,
    runImmediately: true,
    enabled: canAlert,
  });

  // Re-acciona al instante cuando otra pestaña crea/actualiza órdenes (local).
  useStorageSignal("mesas_ordenes", () => {
    if (canAlert) void tick();
  });
}
